import { FC, ReactNode } from 'react'
import clsx from 'clsx'
import { useTranslations } from 'next-intl'

export interface InputWithButtonProps {
  /** 输入框的 register 返回值（来自 react-hook-form） */
  inputProps?: any
  /** 输入框占位符（未传时使用 Common.PleaseEnter 兜底） */
  placeholder?: string
  /** 输入框类型 */
  inputType?: 'text' | 'email' | 'tel' | 'password' | 'number'
  /** 输入框是否禁用 */
  inputDisabled?: boolean
  /** 按钮文本 */
  buttonText: string
  /** 按钮是否禁用 */
  buttonDisabled?: boolean
  /** 按钮是否加载中 */
  isLoading?: boolean
  /** 加载中的文本（未传时使用 Common.Loading 兜底） */
  loadingText?: string
  /** 输入框的自定义类名 */
  inputClassName?: string
  /** 按钮的自定义类名 */
  buttonClassName?: string
  /** 容器的自定义类名 */
  containerClassName?: string
  /** 输入框的宽度（PC版本），H5版本使用 flex-1 */
  inputWidthPC?: string
  /** 按钮的宽度（PC版本） */
  buttonWidthPC?: string
  /** 按钮的宽度（H5版本） */
  buttonWidthH5?: string
  /** 错误信息 */
  error?: string | null
  /** 错误信息的自定义类名 */
  errorClassName?: string
}

/**
 * 输入框和按钮组合组件
 * 支持 PC 和 H5 响应式布局
 */
const InputWithButton: FC<InputWithButtonProps> = ({
  inputProps,
  placeholder,
  inputType = 'text',
  inputDisabled = false,
  buttonText,
  buttonDisabled = false,
  isLoading = false,
  loadingText,
  inputClassName,
  buttonClassName,
  containerClassName,
  inputWidthPC = 'w-[299px]',
  buttonWidthPC = 'w-[102px]',
  buttonWidthH5 = 'w-[100px]',
  error,
  errorClassName,
}) => {
  const t = useTranslations()
  const resolvedPlaceholder = placeholder ?? t('Common.PleaseEnter')
  const resolvedLoadingText = loadingText ?? t('Common.Loading')

  return (
    <div className={clsx('flex flex-col', 'gap-1 laptop:gap-2', containerClassName)}>
      <div className={clsx('flex', 'items-start', 'justify-between')}>
        <input
          {...inputProps}
          type={inputType}
          placeholder={resolvedPlaceholder}
          disabled={inputDisabled || isLoading}
          className={clsx(
            'flex-1 min-w-0',
            inputWidthPC ? `laptop:${inputWidthPC}` : '',
            'h-10 laptop:h-10',
            'bg-pdbg',
            'rounded-[2px]',
            'px-3 laptop:px-4',
            'text-14 laptop:text-14',
            'leading-[21px] laptop:leading-[22px]',
            'text-black/90',
            'placeholder:text-black/40',
            'outline-none',
            'border-none',
            inputClassName
          )}
        />
        <button
          type="submit"
          disabled={buttonDisabled || isLoading}
          className={clsx(
            buttonWidthH5,
            buttonWidthPC ? `laptop:${buttonWidthPC}` : '',
            'h-10 laptop:h-10',
            'bg-pdblue',
            'rounded-[2px]',
            'text-14 laptop:text-base',
            'leading-[21px] laptop:leading-6',
            'font-semibold',
            'text-white',
            'flex-shrink-0',
            'hover:opacity-90',
            'transition-opacity',
            'disabled:opacity-50',
            'disabled:cursor-not-allowed',
            buttonClassName
          )}
        >
          {isLoading ? resolvedLoadingText : buttonText}
        </button>
      </div>

      {error && (
        <p
          className={clsx(
            'text-14 laptop:text-base',
            'leading-[21px] laptop:leading-6',
            'text-[#F52242]',
            errorClassName
          )}
        >
          {error}
        </p>
      )}
    </div>
  )
}

export default InputWithButton
